'use client';

import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { GroqImage } from '@/helpers/Image';
import {
  PageAboutUsSchema,
  PageAboutUsPerformanceCard,
} from '@/sanity/pages/PageAboutUsTemplate';
import { GROQImage } from '@/sanity/lib/definitions';
import { CTAButton } from '@/shadcn/ui/button';
import dotsGradientBg from '@/public/global/dots-gradient-bg.png';
import Image from 'next/image';

const ease = [0.25, 0.1, 0.25, 1] as const;

function ChevronLeft() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      className="h-[2rem] w-[2rem]"
      aria-hidden="true"
    >
      <path
        d="M10 12L6 8l4-4"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      className="h-[2rem] w-[2rem]"
      aria-hidden="true"
    >
      <path
        d="M6 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ImagePanel({ images }: { images: GROQImage[] }) {
  const [[index, direction], setPage] = useState([0, 0]);

  const current = images[index];
  const hasMultiple = images.length > 1;
  const progress = images.length > 1 ? (index + 1) / images.length : 1;

  const paginate = (dir: number) => {
    setPage(([i]) => [(i + dir + images.length) % images.length, dir]);
  };

  return (
    <div className="flex flex-col items-center gap-[2.5rem] w-full">
      {/* Image container */}
      <div className="relative w-full py-[8rem] bg-[#ededed] overflow-hidden flex items-center justify-center">
        {/* Dots texture */}
        <div className="absolute inset-0 z-0 opacity-40 mix-blend-exclusion pointer-events-none select-none">
          <Image fill alt="" src={dotsGradientBg} className="object-cover" />
        </div>

        {/* Screenshot */}
        <AnimatePresence initial={false} custom={direction} mode="popLayout">
          {current && (
            <motion.div
              key={index}
              custom={direction}
              variants={{
                enter: (dir: number) => ({
                  x: dir > 0 ? '60%' : '-60%',
                  opacity: 0,
                }),
                center: { x: 0, opacity: 1 },
                exit: (dir: number) => ({
                  x: dir > 0 ? '-60%' : '60%',
                  opacity: 0,
                }),
              }}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
              className="relative z-10 w-[72%] max-w-[77rem] overflow-hidden rounded-[0.625rem] shadow-xl"
            >
              <GroqImage
                src={current}
                alt=""
                className="!w-full !h-auto !object-cover"
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Navigation */}
      {hasMultiple && (
        <div className="flex items-center gap-[2.5rem] lg:w-[52rem]">
          <button
            onClick={() => paginate(-1)}
            aria-label="Previous image"
            className="grid h-[4.5rem] w-[4.5rem] place-items-center rounded-full border border-black/20 text-black/40 transition-colors hover:bg-black/5 flex-shrink-0"
          >
            <ChevronLeft />
          </button>

          <div className="flex-1 h-[0.5rem] bg-black/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-black transition-all duration-300"
              style={{ width: `${progress * 100}%` }}
            />
          </div>

          <button
            onClick={() => paginate(1)}
            aria-label="Next image"
            className="grid h-[4.5rem] w-[4.5rem] place-items-center rounded-full bg-black text-white transition-colors hover:bg-black/80 flex-shrink-0"
          >
            <ChevronRight />
          </button>
        </div>
      )}
    </div>
  );
}

function PerformanceCard({
  card,
  index,
}: {
  card: PageAboutUsPerformanceCard;
  index: number;
}) {
  const images = card.images ?? [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, ease, delay: index * 0.08 }}
      className="flex flex-col items-center gap-[4rem] lg:gap-[5rem] w-full"
    >
      {card.title && (
        <div className="richtext richtext-center prose-headings:text-center prose-p:text-center prose-headings:text-[2.5rem] lg:prose-headings:text-[3.5rem] prose-headings:leading-[1.4] prose-headings:font-medium prose-headings:tracking-[-0.07rem] lg:prose-headings:tracking-[-0.14rem] prose-p:text-[1.75rem] lg:prose-p:text-[2rem] prose-p:leading-[1.5] prose-p:text-black/70 flex flex-col gap-[1.25rem] lg:gap-[1.75rem] max-w-[71rem]">
          <PortableText value={card.title} components={richTextComponent} />
        </div>
      )}

      {!!images.length && <ImagePanel images={images} />}
    </motion.div>
  );
}

export default function VerifiedPerformanceSection({
  data,
}: {
  data: PageAboutUsSchema;
}) {
  const cards = (data.performanceCards ?? []).slice(0, 2);

  if (!data.performanceChip && !data.performanceTitle && !cards.length)
    return null;

  return (
    <section className="relative w-full bg-white py-[8rem] lg:py-[20rem]">
      <div className="mx-auto flex w-[min(141rem,calc(100%-4rem))] lg:w-[min(141rem,calc(100%-8rem))] flex-col gap-[6rem] lg:gap-[10rem]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease }}
          className="flex flex-col items-center gap-[1.5rem]"
        >
          {data.performanceChip && (
            <p className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-black">
              {data.performanceChip}
            </p>
          )}
          {data.performanceTitle && (
            <div className="richtext">
              <PortableText
                value={data.performanceTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </motion.div>

        {/* Divider */}
        <div className="w-full h-px bg-black/10" />

        {/* Cards */}
        {!!cards.length && (
          <div className="flex flex-col gap-[8rem] lg:gap-[14rem]">
            {cards.map((card, i) => (
              <React.Fragment key={card._key ?? i}>
                {i > 0 && <div className="w-full h-px bg-black/10" />}
                <PerformanceCard card={card} index={i} />
              </React.Fragment>
            ))}
          </div>
        )}

        {data.performanceButton && (
          <div className="w-full lg:w-fit mx-auto">
            <CTAButton data={data.performanceButton} />
          </div>
        )}
      </div>
    </section>
  );
}
